import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChrome } from "../ui/MobileShell";

/* MobileAccount (design 7a) — profile, a verification checklist with the reason
   stated, grouped settings rows, and a destructive Sign out kept out of the
   thumb arc (bottom, outlined red).

   Verification is REAL (GET /kyc/verification + user):
     • Email    — an authenticated PRYMEBET account has a verified email (the
       register flow requires an email code), so this always shows done.
     • Identity — from identity_status / kyc_status; "ID + selfie · needed to
       redeem" with a Start → ID capture (7b) CTA when not yet approved.
     • Phone    — the backend does NOT expose a phone-verification dimension, so
       we only render a Phone step if user.phone exists (never a dead step).
   Pushed screen chrome (back + "Account"). */

const GREEN = "#00D632";

const SETTINGS_ROWS = [
  { ic: "account", l: "Personal details", to: "/account" },
  { ic: "lock", l: "Login & security", to: "/account" },
  { ic: "shield", l: "Responsible play limits", to: "/responsible-play" },
  { ic: "bell", l: "Notifications", to: "/account" },
  { ic: "support", l: "Help & support", to: "/support" },
  { ic: "scale", l: "Terms & sweeps rules", to: "/terms" },
];

export default function MobileAccount() {
  useChrome({ mode: "back", title: "Account" });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verif, setVerif] = useState(null);

  useEffect(() => {
    api.get("/kyc/verification").then(({ data }) => setVerif(data)).catch(() => {});
  }, []);

  const go = (to) => navigate(to);
  const initials = (user?.username || "").slice(0, 2).toUpperCase() || "P";
  const maskedEmail = (() => {
    const e = user?.email || "";
    const [local, domain] = e.split("@");
    if (!domain) return e;
    const head = local.slice(0, 1);
    return `${head}${"•".repeat(Math.max(2, local.length - 1))}@${domain}`;
  })();

  const idStatus = verif?.identity_status || (user?.kyc_status === "approved" ? "approved" : (user?.kyc_status || "not_started"));
  const idApproved = idStatus === "approved";
  const idPending = ["pending_review", "in_progress", "pending"].includes(idStatus);

  // Build the checklist from what the backend actually knows.
  const steps = [
    { key: "email", ic: "mail", l: "Email", sub: user?.email ? maskedEmail : "Add your email", done: !!user?.email, todo: false },
    ...(user?.phone ? [{ key: "phone", ic: "phone", l: "Phone", sub: user.phone, done: true, todo: false }] : []),
    {
      key: "identity", ic: "id-card", l: "Identity",
      sub: idApproved ? "Verified" : idPending ? "Under review" : "ID + selfie · needed to redeem",
      done: idApproved, todo: !idApproved && !idPending, pending: idPending,
    },
  ];
  const stepsLeft = steps.filter((s) => !s.done).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "14px 16px 0" }}>
      {/* profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "2px 2px" }}>
        <span style={{ width: 60, height: 60, flex: "none", borderRadius: "50%", background: "#141417", display: "flex", alignItems: "center", justifyContent: "center", font: "800 22px 'Manrope'", color: "#E4E4E7", boxShadow: "inset 0 0 0 1px #26262B" }}>{initials}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "800 20px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>{user?.username || "Player"}</div>
          <div style={{ font: "500 13px 'Outfit'", color: "#a1a1aa" }}>{maskedEmail}</div>
        </div>
      </div>

      {/* verification card */}
      <div style={{ borderRadius: 22, padding: 16, background: "linear-gradient(180deg,#1A1A1E 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 14px 32px rgba(0,0,0,.4)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ font: "800 16px 'Manrope'", color: "#fff" }}>Verification</span>
          <span style={{ height: 24, display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 999, background: stepsLeft ? "#1C1C21" : "rgba(0,214,50,.14)", font: "700 11px 'Outfit'", color: stepsLeft ? "#a1a1aa" : GREEN }}>
            {stepsLeft ? `${stepsLeft} step${stepsLeft > 1 ? "s" : ""} left` : "All done"}
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          {steps.map((s, i) => (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 54, padding: "8px 0", boxShadow: i ? "inset 0 1px 0 #1F1F23" : undefined }}>
              <Icon name={s.ic} size={20} style={{ color: s.done ? GREEN : "#a1a1aa", flex: "none" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: "700 14px 'Outfit'", color: "#fff" }}>{s.l}</div>
                <div style={{ font: "500 12px 'Outfit'", color: s.todo ? "#a1a1aa" : "#71717a" }}>{s.sub}</div>
              </div>
              {s.done ? (
                <Icon name="check-circle" size={20} style={{ color: GREEN, flex: "none" }} />
              ) : s.pending ? (
                <Icon name="clock" size={18} style={{ color: "#a1a1aa", flex: "none" }} />
              ) : s.key === "identity" ? (
                <button type="button" onClick={() => go("/verify-identity")} data-testid="acct-start-identity"
                  style={{ height: 36, flex: "none", padding: "0 16px", borderRadius: 12, background: GREEN, color: "#0A0A0A", font: "800 13px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)" }}>
                  Start
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* settings list */}
      <div style={{ borderRadius: 16, overflow: "hidden", background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
        {SETTINGS_ROWS.map((r, i) => (
          <button key={r.l} type="button" onClick={() => go(r.to)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, minHeight: 52, padding: "0 14px", background: "none", border: 0, cursor: "pointer", textAlign: "left", boxShadow: i ? "inset 0 1px 0 #1C1C20" : undefined }}>
            <Icon name={r.ic} size={20} style={{ color: "#a1a1aa" }} />
            <span style={{ flex: 1, font: "600 15px 'Outfit'", color: "#fff" }}>{r.l}</span>
            <Icon name="chevron-right" size={16} style={{ color: "#52525b" }} />
          </button>
        ))}
      </div>

      {/* destructive sign out — kept at the bottom, out of the thumb arc */}
      <button type="button" onClick={logout}
        style={{ width: "100%", marginTop: 6, height: 48, borderRadius: 14, background: "transparent", color: "#EF4444", font: "700 15px 'Outfit'", border: 0, cursor: "pointer", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>
        Sign out
      </button>
    </div>
  );
}
